// Edit wind session modal — mirrors legacy showWindEditModal + submitWindEdit.
// Changes apply from the next cycle; current cycle on OKX stays unchanged.
import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { apiPatch } from "@/lib/api-client";
import Modal from "@/components/common/modal";
import type { WindDetailData } from "@/hooks/use-wind-detail";

interface EditWindModalProps {
  data: WindDetailData;
  onClose: () => void;
}

const inputCls =
  "w-full bg-bg border border-border rounded px-2.5 py-1.5 text-sm text-text outline-none focus:border-accent/60 transition-colors";
const primaryBtn =
  "text-xs px-4 py-2 rounded bg-accent/20 border border-accent/50 text-accent hover:bg-accent/30 transition-colors disabled:opacity-50";
const ghostBtn =
  "text-xs px-4 py-2 rounded border border-border text-muted hover:text-text transition-colors";

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <label className="block text-[0.7rem] uppercase tracking-wider text-muted mb-1">
        {label}
      </label>
      {children}
    </div>
  );
}

export default function EditWindModal({ data, onClose }: EditWindModalProps) {
  const { session, latest_cycle } = data;
  const qc = useQueryClient();

  const cycleStatus = latest_cycle?.status;
  // Locked = cycle actively live on exchange — direction/symbol immutable
  const locked = cycleStatus === "open" || cycleStatus === "closing";

  const [tpPct, setTpPct] = useState(String(session.tp_pct));
  const [slPct, setSlPct] = useState(String(session.sl_pct));
  const [holdMin, setHoldMin] = useState(String(session.hold_minutes));
  const [forceMin, setForceMin] = useState(String(session.force_minutes));
  const [autoReopen, setAutoReopen] = useState(Boolean(session.auto_reopen));
  const [aiEnabled, setAiEnabled] = useState(Boolean(session.ai_enabled));
  const [aiAutoAct, setAiAutoAct] = useState(Boolean(session.ai_auto_act));

  const saveMut = useMutation({
    mutationFn: () =>
      apiPatch(`/wind/${session.id}`, {
        tp_pct: parseFloat(tpPct),
        sl_pct: parseFloat(slPct),
        hold_minutes: parseInt(holdMin),
        force_minutes: parseInt(forceMin),
        auto_reopen: autoReopen,
        ai_enabled: aiEnabled,
        ai_auto_act: aiAutoAct,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["wind-detail", session.id] });
      qc.invalidateQueries({ queryKey: ["wind"] });
      onClose();
    },
    onError: (e: Error) => alert(e.message || "Failed to save"),
  });

  return (
    <Modal open title="Edit Wind Session" onClose={onClose}>
      <div className="space-y-3">
        {/* Info banner — always shown */}
        <p className="text-[0.78rem] leading-snug text-info bg-info/10 rounded px-2.5 py-2">
          <span className="opacity-70 mr-1">ⓘ</span>
          Changes apply from the next cycle — current cycle on OKX stays unchanged.
        </p>

        {/* Lock warning — shown only when a cycle is live */}
        {locked && (
          <p className="text-[0.78rem] leading-snug text-warn bg-warn/10 rounded px-2.5 py-2">
            <span className="opacity-85 mr-1">⚠</span>
            Direction &amp; symbol are locked while a cycle is live — config only.
          </p>
        )}

        <div className="grid grid-cols-2 gap-3">
          <Field label="TP %">
            <input
              className={inputCls}
              type="number"
              step="0.01"
              value={tpPct}
              onChange={(e) => setTpPct(e.target.value)}
            />
          </Field>
          <Field label="SL %">
            <input
              className={inputCls}
              type="number"
              step="0.01"
              value={slPct}
              onChange={(e) => setSlPct(e.target.value)}
            />
          </Field>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <Field label="Hold (min)">
            <input
              className={inputCls}
              type="number"
              step="1"
              value={holdMin}
              onChange={(e) => setHoldMin(e.target.value)}
            />
          </Field>
          <Field label="Force exit (min)">
            <input
              className={inputCls}
              type="number"
              step="1"
              value={forceMin}
              onChange={(e) => setForceMin(e.target.value)}
            />
          </Field>
        </div>

        {/* Auto re-open checkbox — inline flex per spec */}
        <label className="inline-flex items-center gap-2 cursor-pointer text-sm text-dim">
          <input
            type="checkbox"
            checked={autoReopen}
            onChange={(e) => setAutoReopen(e.target.checked)}
            className="accent-accent"
          />
          Auto re-open
        </label>

        {/* AI settings — stacked vertically so each toggle has its own line */}
        <div className="space-y-2 pt-2 border-t border-border">
          <label className="flex items-start gap-2 cursor-pointer text-sm text-dim">
            <input
              type="checkbox"
              checked={aiEnabled}
              onChange={(e) => {
                setAiEnabled(e.target.checked);
                // Auto-act is meaningless without advisor; clear it.
                if (!e.target.checked) setAiAutoAct(false);
              }}
              className="accent-accent mt-0.5"
            />
            <span className="flex-1">
              <span className="font-medium text-text">Enable AI advisor</span>
              <span className="block text-[0.72rem] text-muted">
                Sends market data to AI; advice appears in UI (advisory only).
              </span>
            </span>
          </label>
          <label
            className={`flex items-start gap-2 text-sm text-dim ${
              aiEnabled ? "cursor-pointer" : "opacity-50 cursor-not-allowed"
            }`}
          >
            <input
              type="checkbox"
              checked={aiAutoAct}
              onChange={(e) => setAiAutoAct(e.target.checked)}
              className="accent-accent mt-0.5"
              disabled={!aiEnabled}
            />
            <span className="flex-1">
              <span className="font-medium text-text">AI auto-act</span>
              <span className="block text-[0.72rem] text-muted">
                AI automatically executes decisions (cut loss, skip cycle, adjust TP/SL).
              </span>
            </span>
          </label>
          {aiAutoAct && aiEnabled && (
            <p className="text-[0.72rem] text-warn bg-warn/10 rounded px-2 py-1.5">
              <span className="opacity-85 mr-1">⚠</span>
              AI will execute decisions automatically — verify conf thresholds before live use.
            </p>
          )}
        </div>

        <div className="flex gap-2 pt-1">
          <button
            className={primaryBtn}
            disabled={saveMut.isPending}
            onClick={() => saveMut.mutate()}
          >
            {saveMut.isPending ? "Saving…" : "Save"}
          </button>
          <button className={ghostBtn} onClick={onClose}>
            Cancel
          </button>
        </div>
      </div>
    </Modal>
  );
}
